import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { apiRootSelector } from '@schir-int-client/api-root';
import { of } from 'rxjs';
import { catchError, filter, map, mergeMap, switchMap, withLatestFrom } from 'rxjs/operators';
import {
	askToDeleteUSignalAction,
	clearUSignalErrorAction,
	deleteUSignalAction,
	loadAdminRootAction,
	loadAdminRootSuccessAction,
	loadUSignaleAction,
	loadUSignaleSuccessAction,
	saveUsignalErrorAction,
	uploadUSignaleAction,
} from './admin.actions';
import { AdminFacade } from './admin.facade';
import { ErrorType, UnterscheidungssignalListResource } from './admin.model';
import { AdminService } from './admin.service';
import { isNil } from 'lodash-es';
import { doNothingAction } from '@schir-int-client/ngrx-helpers';
import { AdminMessages } from './admin.messages';
import { DialogDataYesNo } from '@schir-int-client/dialog-shared';
import { DialogService } from 'libs/tech/src/lib/dialog.service';
import { AppState } from '../../../../apps/int-client/src/app/shared/store/app-state.model';
import { hasEmbeddedResource } from '@ngxp/rest';
import { UnterscheidungssignalListLinkRel } from './admin.linkrel';

@Injectable()
export class AdminEffects {
	constructor(
		private actions: Actions,
		private adminRootService: AdminService,
		private dialogService: DialogService,
		private facade: AdminFacade,
		private store: Store<AppState>
	) {}

	loadAdminRoot = createEffect(() =>
		this.actions.pipe(
			ofType(loadAdminRootAction),
			withLatestFrom(this.store.select(apiRootSelector)),
			switchMap(([, apiRoot]) =>
				this.adminRootService
					.getAdminRoot(apiRoot)
					.pipe(map(adminRoot => loadAdminRootSuccessAction({ adminRoot })))
			)
		)
	);

	loadUnterscheidungssignale = createEffect(() =>
		this.actions.pipe(
			ofType(loadUSignaleAction),
			withLatestFrom(this.facade.adminRoot),
			filter(([, adminRoot]) => !isNil(adminRoot)),
			switchMap(([, adminRoot]) =>
				this.adminRootService
					.getUnterscheidungssignale(adminRoot)
					.pipe(map(uSignale => loadUSignaleSuccessAction({ uSignale })))
			)
		)
	);

	uploadUnterscheidungssignale = createEffect(() =>
		this.actions.pipe(
			ofType(uploadUSignaleAction),
			withLatestFrom(this.facade.adminRoot),
			switchMap(([{ file }, adminRoot]) => {
				return this.adminRootService.uploadUnterscheidungssignale(adminRoot, file).pipe(
					catchError(error =>
						of(
							saveUsignalErrorAction({
								error: {
									error: error.error,
									type: ErrorType.UNPROCESSABLE_ENTITY,
								},
							})
						)
					),
					mergeMap(uSignale => {
						if (this.instanceOfUSignalResource(uSignale)) {
							return [loadUSignaleSuccessAction({ uSignale }), clearUSignalErrorAction()];
						} else {
							return [uSignale];
						}
					})
				);
			})
		)
	);

	confirmDelete = createEffect(() =>
		this.actions.pipe(
			ofType(askToDeleteUSignalAction),
			switchMap(({ uSignal }) => {
				return this.dialogService
					.openDialogYesNo(AdminMessages.ASK_TO_DELETE_USIGNAL.replace('{usignal}', uSignal.signal))
					.pipe(
						map((dialog: DialogDataYesNo) => {
							if (dialog && dialog.agree) {
								return deleteUSignalAction({ uSignal });
							}
							return doNothingAction();
						})
					);
			})
		)
	);

	delete = createEffect(() =>
		this.actions.pipe(
			ofType(deleteUSignalAction),
			switchMap(({ uSignal }) =>
				this.adminRootService.delete(uSignal).pipe(map(uSignale => loadUSignaleSuccessAction({ uSignale })))
			)
		)
	);

	private instanceOfUSignalResource(object: any): object is UnterscheidungssignalListResource {
		return hasEmbeddedResource(object, UnterscheidungssignalListLinkRel.UNTERSCHEIDUNGSSIGNALE);
	}
}
